import { useRef } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import arrowDown from 'assets/icons/arrow_light_down.svg';
import check from 'assets/icons/check.svg';
import { useToggle } from 'customHooks/useToggle';
import { useOutsideClick } from 'customHooks/useOutsideClick';
import { IconSize } from 'components/common/types/Input.types';
import { ButtonTheme, Color, Size } from 'lib/Button/Button.types';
import {
  DropdownMenuOption,
  DropdownMenuPosition,
  DropdownMenuSection,
  dropdownMenuPositions,
  DropdownMenuProps,
} from './DropdownMenu.types';
import { CircularLoadingSpinner, SpinnerSize } from '../CircularLoadingSpinner/CircularLoadingSpinner';

const IconStyles: {
  size: Record<IconSize.MEDIUM | IconSize.LARGE, string>;
} = {
  size: {
    [IconSize.LARGE]: 'w-6 h-6',
    [IconSize.MEDIUM]: 'w-4 h-4',
  },
};

const getOptionIcon = ({ icon: buttonIcon, label: buttonLabel }: DropdownMenuOption): React.ReactElement | null => {
  if (!buttonIcon) return null;

  return (
    <div data-test-id={`option-icon-${buttonLabel}`}>
      <SVG src={buttonIcon} className="w-4.5 h-4.5" />
    </div>
  );
};

const DropdownMenuOptionsList = ({
  sections,
  shouldCloseOnSelection,
  close,
}: {
  sections: DropdownMenuSection[];
  shouldCloseOnSelection: boolean;
  close: () => void;
}): React.ReactElement => (
  <>
    {sections.map((section) => (
      <div key={section.id} className="flex flex-col">
        {section.label ? (
          <div className="px-4 py-1 body-sm text-neutral-500" data-test-id={`option-section-${section.id}`}>
            {section.label}
          </div>
        ) : null}
        {section.options.map(({ iconPosition = 'left', isSpinner: isButtonSpinner = false, ...buttonOption }) => (
          <button
            className={cx(
              'flex gap-2 items-center body-base text-neutral-950 py-2 rounded-md hover:bg-primary-600-opacity-10',
              section.label ? 'pl-8 pr-4' : 'px-4',
              {
                'opacity-50 cursor-not-allowed': buttonOption.isDisabled,
              },
            )}
            key={buttonOption.id}
            onClick={() => {
              buttonOption.onClick();
              if (shouldCloseOnSelection) close();
            }}
            type="button"
            data-pendo-id={buttonOption.dataPendoId || null}
            disabled={buttonOption.isDisabled}
          >
            {!isButtonSpinner && iconPosition === 'left' && getOptionIcon(buttonOption)}
            {buttonOption.label}
            {buttonOption.isSelected ? (
              <div data-test-id={`option-checkmark-icon-${buttonOption.label}`} className="ml-auto">
                <SVG src={check} className="text-primary-600 w-4 h-4" />
              </div>
            ) : null}
            {!isButtonSpinner && iconPosition === 'right' && getOptionIcon(buttonOption)}
            {isButtonSpinner && (
              <div className="w-4 h-4">
                <CircularLoadingSpinner size={SpinnerSize.EXTRA_SMALL} />
              </div>
            )}
          </button>
        ))}
      </div>
    ))}
  </>
);

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  dropdownMenuOptions = [],
  dropdownMenuSections,
  icon,
  iconSize = IconSize.MEDIUM,
  label,
  dropdownMenuPosition = DropdownMenuPosition.RIGHT,
  color = Color.PRIMARY,
  className,
  isDisabled = false,
  closeOnSelection = false,
  isSpinner = false,
  isMenuFullWidth = false,
  onOpen,
}) => {
  const splitButtonRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const { isOpen, toggle, close } = useToggle(false);

  useOutsideClick(splitButtonRef, close);

  const sections: DropdownMenuSection[] = dropdownMenuSections ?? [{ id: 'default', options: dropdownMenuOptions }];

  return (
    <div className={cx('inline-flex', { relative: !isMenuFullWidth })} ref={splitButtonRef}>
      <button
        className={cx(
          'flex gap-1 items-center sub-header-base leading-none rounded-md disabled:cursor-not-allowed disabled:bg-neutral-300 disabled:text-neutral-400 disabled:hover:bg-neutral-300 disabled:active:bg-neutral-300',
          ButtonTheme.size[Size.MEDIUM],
          ButtonTheme.theme[color],
          className,
        )}
        onClick={() => {
          onOpen?.();
          toggle();
        }}
        type="button"
        data-test-id="selected-option"
        disabled={isDisabled}
      >
        {isSpinner && (
          <div className="w-4 h-4">
            <CircularLoadingSpinner size={SpinnerSize.EXTRA_SMALL} />
          </div>
        )}
        {!isSpinner && icon ? (
          <div data-test-id="button-icon">
            <SVG src={icon} className={IconStyles.size[iconSize]} />
          </div>
        ) : null}
        {label}
        <div data-test-id="arrow-icon">
          <SVG src={arrowDown} className="w-4 h-4" />
        </div>
      </button>
      {isOpen && (
        <div
          className={cx(
            'flex flex-col space-y-1 absolute px-2 mt-[1px] bg-neutral-50 shadow-md rounded-md z-50',
            dropdownMenuPositions[dropdownMenuPosition],
            {
              'inset-x-0 top-10 py-[3px]': isMenuFullWidth,
              'top-9 w-64 py-3': !isMenuFullWidth,
            },
          )}
          data-test-id="options"
        >
          <DropdownMenuOptionsList sections={sections} shouldCloseOnSelection={closeOnSelection} close={close} />
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
